import JSZip from "jszip";
import { Workbook } from "exceljs";

export interface IReportColumn {
  id: string;
  label: string;
}

function byLocalName(parent: Element | Document, localName: string): Element[] {
  const all = parent.getElementsByTagName("*");
  const result: Element[] = [];
  for (const el of Array.from(all)) {
    if (el.localName === localName) result.push(el);
  }
  return result;
}

function colLetterToIndex(col: string): number {
  let result = 0;
  for (const ch of col) {
    result = result * 26 + ((ch.codePointAt(0) ?? 64) - 64);
  }
  return result - 1;
}

function colFromRef(ref: string): number {
  const match = /^([A-Z]+)/.exec(ref);
  return match ? colLetterToIndex(match[1]) : 0;
}

async function readSharedStrings(zip: JSZip): Promise<string[]> {
  const file = zip.file("xl/sharedStrings.xml");
  if (!file) return [];
  const xml = await file.async("string");
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  return byLocalName(doc, "si").map((si) =>
    byLocalName(si, "t")
      .map((t) => t.textContent ?? "")
      .join("")
  );
}

function findSheetFile(zip: JSZip): JSZip.JSZipObject | null {
  const standard = zip.file("xl/worksheets/sheet1.xml");
  if (standard) return standard;
  const path = Object.keys(zip.files).find((f) =>
    /^xl\/worksheets\/sheet\d+\.xml$/.test(f)
  );
  return path ? zip.file(path) : null;
}

function resolveCellValue(cellNode: Element, sharedStrings: string[]): string {
  const type = cellNode.getAttribute("t") ?? "n";
  const vNode = byLocalName(cellNode, "v")[0];

  if (type === "s") {
    const idx = Number.parseInt(vNode?.textContent ?? "0", 10);
    return sharedStrings[idx] ?? "";
  }
  if (type === "inlineStr") {
    return byLocalName(cellNode, "t")[0]?.textContent ?? "";
  }
  if (type === "b") {
    return vNode?.textContent === "1" ? "TRUE" : "FALSE";
  }
  return vNode?.textContent ?? "";
}

export async function parseExcelBlob(
  blob: Blob
): Promise<{ columns: IReportColumn[]; rows: Record<string, any>[] }> {
  const buf = await blob.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);
  const sharedStrings = await readSharedStrings(zip);

  const sheetFile = findSheetFile(zip);
  if (!sheetFile) return { columns: [], rows: [] };

  const sheetXml = await sheetFile.async("string");
  const doc = new DOMParser().parseFromString(sheetXml, "text/xml");
  const rowNodes = byLocalName(doc, "row");

  const parsedRows = rowNodes.map((rowNode) => {
    const cellNodes = byLocalName(rowNode, "c");
    const cells: Array<{ col: number; value: string }> = [];
    let seqCol = 0;
    for (const cell of cellNodes) {
      const ref = cell.getAttribute("r");
      const col = ref ? colFromRef(ref) : seqCol;
      const value = (resolveCellValue(cell, sharedStrings) ?? "").trim();
      cells.push({ col, value });
      seqCol = col + 1;
    }
    const nonEmptyCount = cells.filter((c) => c.value !== "").length;
    return { cells, nonEmptyCount };
  });

  const headerRowIndex = (() => {
    const firstMultiColumnRow = parsedRows.findIndex((r) => r.nonEmptyCount > 1);
    if (firstMultiColumnRow >= 0) return firstMultiColumnRow;
    return parsedRows.findIndex((r) => r.nonEmptyCount > 0);
  })();

  if (headerRowIndex < 0) return { columns: [], rows: [] };

  const columns: IReportColumn[] = [];
  const headerIds: string[] = [];
  for (const cell of parsedRows[headerRowIndex].cells) {
    if (!cell.value) continue;
    const id = `col_${cell.col + 1}`;
    if (!headerIds[cell.col]) {
      columns.push({ id, label: cell.value || `Col ${cell.col + 1}` });
      headerIds[cell.col] = id;
    }
  }

  const rows: Record<string, any>[] = [];
  for (let rowIdx = headerRowIndex + 1; rowIdx < parsedRows.length; rowIdx++) {
    const record: Record<string, any> = {};
    headerIds.forEach((id) => {
      if (id) record[id] = "";
    });

    let hasAnyValue = false;
    for (const cell of parsedRows[rowIdx].cells) {
      const id = headerIds[cell.col];
      if (!id) continue;
      record[id] = cell.value;
      if (cell.value !== "") hasAnyValue = true;
    }

    if (hasAnyValue) rows.push(record);
  }

  return { columns, rows };
}

export async function exportToExcel(
  columns: IReportColumn[],
  rows: Record<string, any>[],
  fileName: string
): Promise<void> {
  const wb = new Workbook();
  const ws = wb.addWorksheet("Report");

  const headerRow = ws.addRow(columns.map((c) => c.label));
  headerRow.font = { bold: true };

  rows.forEach((row) => {
    ws.addRow(columns.map((c) => row[c.id] ?? ""));
  });

  ws.columns.forEach((col) => {
    col.width = 20;
  });

  const buffer = await wb.xlsx.writeBuffer();
  const output = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(output);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}
